import { Avatar, Box, Paper, Stack, Typography } from "@mui/material";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { Link as RouterLink } from "react-router-dom";
import { authorInitials, authorLabel, MISSING } from "@bw/shared/components";
import type {
  MediaEmbed,
  MessageResponse,
  PostResponse,
} from "@bw/shared/types/generated";

/**
 * SharedPostEmbed — the DM-thread rendering of a post SHARED into a direct message
 * (the "Send" affordance's real payload). Shared by BOTH player messaging surfaces
 * — the full-page thread (`MessagesLayout`) and the docked widget (`MessagingDock`)
 * — so a shared post looks identical wherever it lands. Given a `MessageResponse`
 * it renders one of three things, strictly from the SERVER's fields:
 *
 *   1. `shared_post` present  → a COMPACT embedded post card: the original author
 *      (uid-safe name via `authorLabel` + an initials chip, NEVER the raw uid), a
 *      short 1–2 line plain-text snippet of the body, and ONE small media thumbnail
 *      when the post has renderable typed media. The WHOLE card is a `<Link>` to
 *      `/community/posts/{post_id}` (in-app navigation — the uid may sit in the HREF,
 *      which is a URL, never visible text).
 *   2. `shared_post_unavailable` (with a `shared_post_id`) → a muted TOMBSTONE
 *      ("This post is no longer available.") — no content, no author, no link. A
 *      deleted / blocked / inaccessible original leaks nothing. A present-but-
 *      soft-deleted original (`shared_post.is_deleted`) renders the SAME tombstone
 *      (defense-in-depth, mirroring PostCard) — never the (empty) content.
 *   3. neither → renders NOTHING (a normal text-only message).
 *
 * HARDENING: the snippet is PLAIN TEXT (whitespace-collapsed, length-capped —
 * never markdown / HTML, no `dangerouslySetInnerHTML`); the thumbnail is the
 * SERVER's TYPED media only (`media_type` image → its url, youtube → its
 * `thumbnail_url`), rendered as a decorative <img> with `referrerPolicy` +
 * `loading="lazy"` — an unknown/whitespace media type yields no thumbnail. There
 * is no raw-url parsing anywhere; a crafted url can only ever land in an escaped
 * `src` attribute.
 */
export function SharedPostEmbed({ message }: { message: MessageResponse }) {
  const shared = message.shared_post ?? null;

  // A present-AND-live original → the compact card. A soft-deleted original
  // (`shared_post.is_deleted`) falls through to the tombstone below — NEVER the
  // (empty) content.
  if (shared && !shared.is_deleted) {
    return <SharedPostCard post={shared} />;
  }

  // The original is gone (deleted / blocked / not visible) but the message still
  // references it → an honest tombstone, never the (absent) content. Two triggers:
  //   - the server's `shared_post_unavailable` flag (the primary signal), OR
  //   - a defense-in-depth client check on `shared_post.is_deleted` — the server
  //     already tombstones deleted posts via the flag, but this belt-and-suspenders
  //     check mirrors PostCard's soft-deleted-original tombstone and costs nothing
  //     (a crafted/stale payload carrying a deleted post can never leak its body).
  if (
    shared?.is_deleted ||
    (message.shared_post_unavailable && message.shared_post_id)
  ) {
    return (
      <Paper
        component="span"
        variant="outlined"
        sx={{ display: "block", p: 1.5, mt: 1 }}
      >
        <SharedPostEyebrow />
        <Typography component="span" variant="body2" color="text.secondary">
          This post is no longer available.
        </Typography>
      </Paper>
    );
  }

  // No shared post on this message → nothing extra to render.
  return null;
}

/** The max plain-text snippet length woven into the compact card (CSS clamps the
 *  visible height to ~2 lines; this bounds the DOM text regardless). */
const SNIPPET_MAX = 160;

/**
 * The compact embedded card for an available shared post. A single `<Link>` to the
 * post-detail route holds the author identity row, a plain-text snippet, and an
 * optional single media thumbnail. No nested interactive elements (the media is a
 * static <img>, not an iframe), so the whole card is a valid single anchor.
 */
function SharedPostCard({ post }: { post: PostResponse }) {
  const name = authorLabel(post.author_display_name, post.author_id);
  const initials = authorInitials(post.author_display_name);
  const snippet = snippetOf(post.content);
  const thumb = firstThumbnail(post.media ?? []);

  return (
    <Paper
      component={RouterLink}
      to={`/community/posts/${post.post_id}`}
      variant="outlined"
      aria-label={
        snippet && name !== MISSING
          ? `Shared post by ${name}: ${snippet}`
          : name !== MISSING
            ? `Shared post by ${name}`
            : "Shared post"
      }
      sx={{
        display: "block",
        p: 1.5,
        mt: 1,
        color: "text.primary",
        textDecoration: "none",
      }}
    >
      <SharedPostEyebrow />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", my: 1 }}>
        {initials ? (
          <Avatar
            aria-hidden
            sx={{ width: 28, height: 28, fontSize: "0.75rem" }}
          >
            {initials}
          </Avatar>
        ) : (
          <Avatar aria-hidden sx={{ width: 28, height: 28 }} />
        )}
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {snippet ? (
          <Typography
            component="span"
            variant="body2"
            sx={{
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {snippet}
          </Typography>
        ) : null}
        {thumb ? (
          <Box
            component="img"
            src={thumb}
            alt=""
            aria-hidden="true"
            referrerPolicy="no-referrer"
            loading="lazy"
            sx={{ width: 52, height: 52, borderRadius: 1, objectFit: "cover" }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * The "Shared a post" eyebrow that heads every embed (the live card AND the
 * tombstone), so a shared post reads clearly as a SHARED POST — not a plain
 * message — regardless of the bubble colour it sits on. Decorative (aria-hidden):
 * the card's own aria-label already announces "Shared post by …", and the
 * tombstone's text says it is unavailable, so the eyebrow adds no SR noise.
 */
function SharedPostEyebrow() {
  return (
    <Stack
      component="span"
      direction="row"
      spacing={0.5}
      aria-hidden
      sx={{ alignItems: "center" }}
    >
      <RepeatRoundedIcon sx={{ fontSize: 14 }} />
      Shared a post
    </Stack>
  );
}

/** A single-line, whitespace-collapsed, length-capped plain-text excerpt of the
 *  post body (never markdown / HTML). Empty content yields "". */
function snippetOf(content: string | null | undefined): string {
  const collapsed = (content ?? "").replace(/\s+/g, " ").trim();
  if (collapsed.length <= SNIPPET_MAX) return collapsed;
  return `${collapsed.slice(0, SNIPPET_MAX - 1).trimEnd()}…`;
}

/**
 * The src of the FIRST renderable typed-media thumbnail, or null. An `image` uses
 * its own url; a `youtube` uses the server's poster `thumbnail_url` (never a
 * derived/raw url); anything else (unknown type, or a youtube with no poster) is
 * skipped — fail-closed, exactly like the shared PostMedia collage.
 */
function firstThumbnail(media: MediaEmbed[]): string | null {
  for (const m of media) {
    if (m.media_type === "image" && m.url) return m.url;
    if (m.media_type === "youtube" && m.thumbnail_url) return m.thumbnail_url;
  }
  return null;
}
